import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import { FC } from 'react';
import { useRoutesAndScheduleState } from '../../../contexts/routesAndSchedule.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TTransactionCurrency } from 'src/apis/finance/finance.interfaces';

const SelectedRouteDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedRoute, vehicles, drivers } = useRoutesAndScheduleState();

  if (!selectedRoute) {
    return (
      <Box
        sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
        data-testid="route-details-container"
      >
        <Typography>
          {translateText('administration.routesAndSchedule.noData')}
        </Typography>
      </Box>
    );
  }

  const assignedVehicle = vehicles.find(
    (v) => v.id === selectedRoute.assignedVehicleId
  );
  const assignedDriver = drivers.find(
    (d) => d.id === selectedRoute.assignedDriverId
  );

  const formatCurrency = (
    amount: number,
    currency: TTransactionCurrency = TTransactionCurrency.INR
  ) => {
    const currencyMap: Record<TTransactionCurrency, string> = {
      [TTransactionCurrency.USD]: 'en-US',
      [TTransactionCurrency.EUR]: 'de-DE',
      [TTransactionCurrency.GBP]: 'en-GB',
      [TTransactionCurrency.INR]: 'en-IN',
    };

    return new Intl.NumberFormat(currencyMap[currency], {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Box
      sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
      data-testid="route-details-container"
    >
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        {translateText('administration.routesAndSchedule.columns.routeName')}:{' '}
        {selectedRoute.routeName}
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.routesAndSchedule.details.basicInfo'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="route-start-location">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.startLocation'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedRoute.startLocation}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="route-end-location">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.endLocation'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedRoute.endLocation}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="route-distance">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.distance'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedRoute.distance} km
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="route-duration">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.duration'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedRoute.estimatedDuration} min
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="route-stops">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.numberOfStops'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedRoute.stops?.length || 0}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="route-status">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText(
                  'administration.routesAndSchedule.columns.status'
                )}
              </Typography>
              <Chip
                label={
                  selectedRoute.isActive
                    ? translateText(
                        'administration.routesAndSchedule.status.active'
                      )
                    : translateText(
                        'administration.routesAndSchedule.status.inactive'
                      )
                }
                color={selectedRoute.isActive ? 'success' : 'default'}
                size="small"
              />
            </Grid>
            {assignedVehicle?.pricePerKilometer !== undefined && (
              <Grid item xs={12} sm={6} md={4} data-testid="route-price-per-km">
                <Typography variant="caption" color="textSecondary">
                  {translateText(
                    'administration.routesAndSchedule.columns.pricePerKilometer'
                  )}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatCurrency(
                    assignedVehicle.pricePerKilometer,
                    assignedVehicle.priceCurrency || TTransactionCurrency.INR
                  )}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText('administration.routesAndSchedule.details.schedule')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} data-testid="route-morning-pickup">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.morningPickup'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedRoute.morningPickupTime}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} data-testid="route-afternoon-drop">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.afternoonDrop'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedRoute.afternoonDropTime}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.routesAndSchedule.details.assignments'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} data-testid="route-vehicle">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.vehicle'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignedVehicle?.vehicleNumber ||
                  translateText(
                    'administration.routesAndSchedule.status.unassigned'
                  )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} data-testid="route-driver">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.driver'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignedDriver?.name ||
                  translateText(
                    'administration.routesAndSchedule.status.unassigned'
                  )}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedRoute.stops && selectedRoute.stops.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              {translateText('administration.routesAndSchedule.details.stops')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {selectedRoute.stops.map((stop, index) => (
                <Grid
                  item
                  xs={12}
                  key={stop.id}
                  data-testid={`route-stop-${index}`}
                >
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'background.default',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {stop.stopName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {stop.location}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                      <Typography variant="caption">
                        {translateText(
                          'administration.routesAndSchedule.details.pickupTime'
                        )}
                        : {stop.pickupTime}
                      </Typography>
                      <Typography variant="caption">
                        {translateText(
                          'administration.routesAndSchedule.details.dropTime'
                        )}
                        : {stop.dropTime}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SelectedRouteDetailsView;
